import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";
import type { Couple, UserProfile } from "../types";

interface CoupleState {
  loading: boolean;
  profile: UserProfile | null;
  couple: Couple | null;
  partner: UserProfile | null;
}

// Pairing state is never cached on the user profile — it's derived live from
// the couples collection (memberUids array-contains my uid), so there is
// nothing that can ever drift out of sync between the two partners.
export function useCouple(): CoupleState {
  const { user, initializing } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [couple, setCouple] = useState<Couple | null>(null);
  const [coupleLoaded, setCoupleLoaded] = useState(false);
  const [partner, setPartner] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (initializing) return;
    if (!user) {
      setProfile(null);
      setProfileLoaded(true);
      return;
    }
    setProfileLoaded(false);
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setProfile(snap.exists() ? ({ uid: snap.id, ...snap.data() } as UserProfile) : null);
      setProfileLoaded(true);
    });
    return unsub;
  }, [user, initializing]);

  useEffect(() => {
    if (!user) {
      setCouple(null);
      setCoupleLoaded(true);
      return;
    }
    setCoupleLoaded(false);
    const q = query(collection(db, "couples"), where("memberUids", "array-contains", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const first = snap.docs[0];
      setCouple(first ? ({ id: first.id, ...first.data() } as Couple) : null);
      setCoupleLoaded(true);
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!couple || !user) {
      setPartner(null);
      return;
    }
    const partnerUid = couple.memberUids.find((uid) => uid !== user.uid);
    if (!partnerUid) {
      setPartner(null);
      return;
    }
    const unsub = onSnapshot(doc(db, "users", partnerUid), (snap) => {
      setPartner(snap.exists() ? ({ uid: snap.id, ...snap.data() } as UserProfile) : null);
    });
    return unsub;
  }, [couple, user]);

  return { loading: initializing || !profileLoaded || !coupleLoaded, profile, couple, partner };
}
